import React, { useEffect, useRef, useState } from "react";
import { useParams, useSearchParams, Link } from "react-router-dom";
import api from "../api";
import "./RequestPage.css";
import { FaInstagram } from "react-icons/fa";
import { openExternal } from "../lib/openExternal";
import { FaMagnifyingGlass as FaSearch } from "react-icons/fa6";
import { FaBookmark } from "react-icons/fa";
import { roundPriceStep } from "../utils/roundPriceStep";
import { loadStripe } from "@stripe/stripe-js";
import {
  EmbeddedCheckout,
  EmbeddedCheckoutProvider,
  Elements,
} from "@stripe/react-stripe-js";
import InlinePayOptions from "../components/InlinePayOptions";
import ThreeStepIndicator from "../components/ThreeStepIndicator";
import "../components/ThreeStepIndicator.css";

type FeedTab = "for_you" | "trending" | "saved";

const STRIPE_PK = process.env.REACT_APP_STRIPE_PUBLISHABLE_KEY || "";
const isSecure = window.location.protocol === "https:" || window.location.hostname === "localhost";
const stripePromise = STRIPE_PK && isSecure ? loadStripe(STRIPE_PK) : null;

type Track = {
  id: string;
  name: string;
  artists: { name: string }[];
  album: string;
  preview_url: string | null;
  image: string;
};

const truncate = (s: string, n: number) =>
  (s?.length || 0) > n ? s.slice(0, n - 1) + "…" : s;
const round2 = (n: number) => Math.round(n * 100) / 100;

const parseGuestIdentity = (value: string) => {
  const trimmed = value.trim();
  if (!trimmed) return { nickname: "", instagram: "" };
  const atEnd = trimmed.match(/(?:^|\s)@([a-zA-Z0-9._]{1,30})\s*$/);
  if (!atEnd) {
    const atOnly = trimmed.match(/^@([a-zA-Z0-9._]{1,30})$/);
    return atOnly
      ? { nickname: "", instagram: atOnly[1] }
      : { nickname: trimmed.slice(0, 50), instagram: "" };
  }
  return {
    nickname: trimmed.replace(atEnd[0], "").trim().slice(0, 50),
    instagram: atEnd[1],
  };
};

type Tier = "standard" | "priority" | "fast";
type PricingMode = "tile" | "custom";

/** === Быстрый фид === */
const FEED_SIZE = 45;
const CACHE_TRENDING = "rq_cache_trending_v1";
const CACHE_FORYOU = "rq_cache_foryou_v1"; // хранит последнюю "for_you"

const RANDOM_QUERIES = [
  "love", "you", "the", "night", "dance", "party", "summer", "baby",
  "rock", "pop", "rap", "house", "moon", "blue", "time", "life", "heart", "fire",
];
const pickRand = <T,>(arr: T[]) => arr[Math.floor(Math.random() * arr.length)];

const RequestPage: React.FC = () => {
  const { slug } = useParams<{ slug: string }>();
  const [dj, setDj] = useState<any>(null);
  const [showOfflineBlock, setShowOfflineBlock] = useState(false);
  const [activeTab, setActiveTab] = useState<FeedTab>("for_you");

  /** Saved */
  const [savedIds, setSavedIds] = useState<string[]>(
    () => JSON.parse(localStorage.getItem("rq_saved_tracks") || "[]")
  );

  /** Фиды/поиск */
  const [feed, setFeed] = useState<Track[]>([]);
  const [loadingFeed, setLoadingFeed] = useState(false);
  const [searchQuery, setSearchQuery] = useState("");
  const [results, setResults] = useState<Track[]>([]);
  const [selectedTrack, setSelectedTrack] = useState<Track | null>(null);

  /** Платежи */
  const [baseAmount, setBaseAmount] = useState<string>("");
  const [createdOrderId, setCreatedOrderId] = useState<string | null>(null);
  const [tier, setTier] = useState<Tier>("standard");
  const [customOpen, setCustomOpen] = useState(false);
  const [customEdit, setCustomEdit] = useState(false);
  const customInputRef = useRef<HTMLInputElement | null>(null);
  const [comment, setComment] = useState("");
  const [commentEnabled, setCommentEnabled] = useState(false);
  const [showInfo, setShowInfo] = useState(false);

  const [showPayInline, setShowPayInline] = useState(false);
  const [payInlineLoading, setPayInlineLoading] = useState(false);
  const [piClientSecret, setPiClientSecret] = useState<string | null>(null);
  const [showCardForm, setShowCardForm] = useState(false);
  const [emailForLink, setEmailForLink] = useState("");
  const [processing, setProcessing] = useState(false);
  const [payError, setPayError] = useState<string | null>(null);

  const [showCheckout, setShowCheckout] = useState(false);
  const [embeddedSessionId, setEmbeddedSessionId] = useState<string | null>(null);
  const [fetchClientSecret, setFetchClientSecret] = useState<(() => Promise<string>) | null>(null);

  const [tipMode, setTipMode] = useState(false);
  const [tipAmount, setTipAmount] = useState("5");
  const [tipOrderId, setTipOrderId] = useState<string | null>(null);
  const [tipClientSecret, setTipClientSecret] = useState<string | null>(null);
  const [tipShowPay, setTipShowPay] = useState(false);
  const [tipShowCard, setTipShowCard] = useState(false);
  const [tipProcessing, setTipProcessing] = useState(false);
  const [tipPayError, setTipPayError] = useState<string | null>(null);
  const tipPayRef = useRef<HTMLDivElement | null>(null);
  const tipDebounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const [pricingMode, setPricingMode] = useState<PricingMode>("tile");
  const [bidFocused, setBidFocused] = useState(false);
  const [bidTouched, setBidTouched] = useState(false);

  const [clientNickname, setClientNickname] = useState<string>(
    () => sessionStorage.getItem("rq_client_nickname") || ""
  );
  const [clientInstagram, setClientInstagram] = useState<string>(
    () => sessionStorage.getItem("rq_client_instagram") || ""
  );
  const [guestIdentityInput, setGuestIdentityInput] = useState<string>(() => {
    const n = sessionStorage.getItem("rq_client_nickname") || "";
    const ig = sessionStorage.getItem("rq_client_instagram") || "";
    if (n && ig) return `${n} @${ig}`;
    if (ig) return `@${ig}`;
    return n;
  });
  const [showNicknamePrompt, setShowNicknamePrompt] = useState(() => {
    return !sessionStorage.getItem("rq_client_nickname") && !sessionStorage.getItem("rq_client_instagram");
  });

  const resultsRef = useRef<HTMLUListElement | null>(null);
  const searchInputRef = useRef<HTMLInputElement | null>(null);
  const [justSelected, setJustSelected] = useState(false);
  const [searchParams] = useSearchParams();
  const BANNER_TTL_MS = 8 * 60 * 60 * 1000;
  const PREV_ORDERS_KEY = "rq_prev_orders";
  type PrevOrderData = { status: string; searchQuery: string; tier?: string; trackImage?: string | null };
  const prevOrderIds: string[] = (() => {
    let entries: { id: string; ts: number }[] = [];
    try { entries = JSON.parse(localStorage.getItem(PREV_ORDERS_KEY) || "[]"); } catch {}
    const incoming = searchParams.get("prev_order_id");
    if (incoming) {
      entries = entries.filter(e => e.id !== incoming);
      entries.unshift({ id: incoming, ts: Date.now() });
      entries = entries.slice(0, 3);
      localStorage.setItem(PREV_ORDERS_KEY, JSON.stringify(entries));
    }
    const valid = entries.filter(e => Date.now() - e.ts <= BANNER_TTL_MS);
    if (valid.length !== entries.length) localStorage.setItem(PREV_ORDERS_KEY, JSON.stringify(valid));
    return valid.map(e => e.id);
  })();
  const [prevOrders, setPrevOrders] = useState<Record<string, PrevOrderData>>({});

  const inlinePayRef = useRef<HTMLDivElement | null>(null);
  const piDebounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  // Используем только для отображения валютного символа (на сервер НЕ отправляем)
  const currency = (localStorage.getItem("currency") || "usd").toLowerCase();

  /** Кэши в памяти */
  const searchCache = useRef<Map<string, Track[]>>(new Map());
  const feedCache = useRef<{ for_you?: Track[]; trending?: Track[]; saved?: Track[] }>({});

  const [notFound, setNotFound] = useState(false);

  /** Poll previous order statuses */
  useEffect(() => {
    if (!prevOrderIds.length) return;
    const poll = () => {
      prevOrderIds.forEach(id => {
        api.get(`/orders/order-status/${id}`)
          .then(res => setPrevOrders(prev => ({ ...prev, [id]: res.data })))
          .catch(() => {});
      });
    };
    poll();
    const interval = setInterval(poll, 5000);
    return () => clearInterval(interval);
  }, []);

  /** === DJ === */
  useEffect(() => {
    if (!slug) return;
    api.get(`/users/${slug}`)
      .then((res) => setDj(res.data))
      .catch((err) => {
        if (err.response?.status === 404 || err.response?.status === 400) {
          setNotFound(true);
        } else {
          console.error("Failed to load DJ", err);
          setNotFound(true);
        }
      });
  }, [slug]);

  /** === Поиск: отмена предыдущего + кэш === */
  useEffect(() => {
    if (searchQuery.length < 2 || selectedTrack?.name === searchQuery) {
      setResults([]);
      return;
    }
    const key = searchQuery.trim().toLowerCase();
    const cached = searchCache.current.get(key);
    if (cached) {
      setResults(cached);
      return;
    }

    const ctrl = new AbortController();
    const t = setTimeout(async () => {
      try {
        const { data } = await api.get<{ tracks: Track[] }>(
          `/spotify/search?q=${encodeURIComponent(key)}`,
          { signal: ctrl.signal } as any
        );
        searchCache.current.set(key, data.tracks);
        setResults(data.tracks);
      } catch (e: any) {
        if (e.name !== "CanceledError" && e.name !== "AbortError") {
          console.error("Spotify search error", e);
        }
      }
    }, 300);
    return () => {
      clearTimeout(t);
      ctrl.abort();
    };
  }, [searchQuery, selectedTrack]);


  /** Закрытие выпадашки по клику вне */
  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (justSelected) {
        setJustSelected(false);
        return;
      }
      if (resultsRef.current && !resultsRef.current.contains(e.target as Node)) {
        setResults([]);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, [justSelected]);

  /** Фокус на поле поиска при входе/очистке */
  useEffect(() => {
    const t = setTimeout(() => {
      searchInputRef.current?.focus();
      searchInputRef.current?.click();
    }, 200);
    return () => clearTimeout(t);
  }, []);

  /** Параметр кастомной цены в URL */
  useEffect(() => {
    const na = searchParams.get("new_amount");
    if (na && !isNaN(Number(na))) {
      setBaseAmount(String(Number(na)));
      setPricingMode("custom");
    }
  }, [searchParams]);



  /** Saved toggle */
  const toggleSaved = (id: string) => {
    setSavedIds((prev) => {
      const next = prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id];
      localStorage.setItem("rq_saved_tracks", JSON.stringify(next));
      feedCache.current.saved = undefined;
      return next;
    });
  };

  /** === Быстрая отдача из sessionStorage === */
  const instantFromSession = (key: string): Track[] | null => {
    try {
      const raw = sessionStorage.getItem(key);
      if (!raw) return null;
      const arr = JSON.parse(raw) as Track[];
      return Array.isArray(arr) && arr.length ? arr.slice(0, FEED_SIZE) : null;
    } catch {
      return null;
    }
  };

  const saveToSession = (key: string, value: Track[]) => {
    try {
      sessionStorage.setItem(key, JSON.stringify(value.slice(0, FEED_SIZE)));
    } catch { }
  };

  /** === Запасной план === */
  const fetchRandomTracks = async (n = FEED_SIZE): Promise<Track[]> => {
    const qs = Array.from({ length: 4 }, () => pickRand(RANDOM_QUERIES));
    try {
      const results = await Promise.all(
        qs.map((q) => api.get<{ tracks: Track[] }>(`/spotify/search?q=${encodeURIComponent(q)}`))
      );
      const uniq = new Map<string, Track>();
      for (const r of results) {
        for (const t of r.data.tracks) {
          if (!uniq.has(t.id)) uniq.set(t.id, t);
          if (uniq.size >= n) break;
        }
        if (uniq.size >= n) break;
      }
      return Array.from(uniq.values()).slice(0, n);
    } catch {
      return [];
    }
  };

  /** === Основная загрузка фида с SWR === */
  const loadFeed = async (tab: FeedTab) => {
    const mem = feedCache.current[tab];
    if (mem && mem.length) {
      setFeed(mem.slice(0, FEED_SIZE));
      setLoadingFeed(false);
    } else {
      if (tab === "trending") {
        const ss = instantFromSession(CACHE_TRENDING);
        if (ss) { setFeed(ss); setLoadingFeed(false); } else setLoadingFeed(true);
      } else if (tab === "for_you") {
        const ss = instantFromSession(CACHE_FORYOU) || instantFromSession(CACHE_TRENDING);
        if (ss) { setFeed(ss); setLoadingFeed(false); } else setLoadingFeed(true);
      } else {
        setLoadingFeed(true);
      }
    }

    try {
      if (tab === "saved") {
        if (!savedIds.length) { setFeed([]); feedCache.current.saved = []; setLoadingFeed(false); return; }
        try {
          const { data } = await api.get<{ tracks: Track[] }>(`/spotify/by-ids?ids=${savedIds.join(",")}`);
          const next = (data.tracks || []).slice(0, FEED_SIZE);
          setFeed(next); feedCache.current.saved = next;
        } catch { setFeed([]); feedCache.current.saved = []; }
        finally { setLoadingFeed(false); }
        return;
      }

      if (tab === "trending") {
        try {
          const { data } = await api.get<{ tracks: Track[] }>(`/spotify/trending?limit=${FEED_SIZE}`);
          const next = (data.tracks || []).slice(0, FEED_SIZE);
          setFeed(next); feedCache.current.trending = next; saveToSession(CACHE_TRENDING, next);
        } catch {
          const fallback = await fetchRandomTracks(FEED_SIZE);
          setFeed(fallback); feedCache.current.trending = fallback;
        } finally { setLoadingFeed(false); }
        return;
      }

      const ordered = JSON.parse(localStorage.getItem("rq_ordered_tracks") || "[]") as string[];
      if (ordered.length) {
        try {
          const seed = ordered.slice(-5);
          const { data } = await api.get<{ tracks: Track[] }>(
            `/spotify/recommend?seed_tracks=${seed.join(",")}&limit=${FEED_SIZE}`
          );
          const next = (data.tracks || []).slice(0, FEED_SIZE);
          setFeed(next); feedCache.current.for_you = next; saveToSession(CACHE_FORYOU, next);
        } catch {
          try {
            const { data } = await api.get<{ tracks: Track[] }>(`/spotify/trending?limit=${FEED_SIZE}`);
            const next = (data.tracks || []).slice(0, FEED_SIZE);
            setFeed(next); feedCache.current.for_you = next; saveToSession(CACHE_FORYOU, next);
          } catch {
            const fallback = await fetchRandomTracks(FEED_SIZE);
            setFeed(fallback); feedCache.current.for_you = fallback;
          }
        } finally { setLoadingFeed(false); }
      } else {
        try {
          const { data } = await api.get<{ tracks: Track[] }>(`/spotify/trending?rand=1&limit=${FEED_SIZE}`);
          const next = (data.tracks || []).slice(0, FEED_SIZE);
          setFeed(next); feedCache.current.for_you = next; saveToSession(CACHE_FORYOU, next);
        } catch {
          const fallback = await fetchRandomTracks(FEED_SIZE);
          setFeed(fallback); feedCache.current.for_you = fallback;
        } finally { setLoadingFeed(false); }
      }
    } catch (e) {
      console.error("feed error", e);
      const fallback = await fetchRandomTracks(FEED_SIZE);
      setFeed(fallback); feedCache.current[tab] = fallback; setLoadingFeed(false);
    }
  };

  useEffect(() => {
    if (!selectedTrack) loadFeed(activeTab);
  }, [activeTab, selectedTrack, JSON.stringify(savedIds)]);

  useEffect(() => {
    if (!selectedTrack) {
      const t = setTimeout(() => { searchInputRef.current?.focus(); }, 120);
      return () => clearTimeout(t);
    }
  }, [selectedTrack]);

  /** Цены/Fees */
  const minPrice = Number.isFinite(Number(dj?.pricePerRequest))
    ? Number(dj.pricePerRequest)
    : 0;
  const isFree = minPrice === 0;

  // базовые значения, которые показываем в шапке
  const djMinBase = roundPriceStep(minPrice || 0);
  const fastPassBase = roundPriceStep((minPrice || 0) * 2);

  const MULT: Record<Tier, number> = { standard: 1, priority: 1.5, fast: 2 };
  const baseForTiles = minPrice;


  // БАЗА = сколько "стоит запрос" у диджея (с учётом tier)
  const requestBase = (() => {
    if (isFree) return 0;
    const raw = Number(baseAmount || minPrice);
    const clamped = isNaN(raw) ? minPrice : Math.max(minPrice, raw);
    return roundPriceStep(clamped);
  })();


  // Fee сайта – единая ставка, например 15% от базы
  const SITE_FEE_RATE = 0.15;
  const siteFee = isFree || requestBase <= 0
    ? 0
    : round2(requestBase * SITE_FEE_RATE);

  // Итог, который видит и платит клиент
  const totalDisplay = isFree
    ? 0
    : roundPriceStep(requestBase + siteFee);

  // Для попапа "Request / Fees / Total"
  const requestShown = requestBase;
  const feesShown = siteFee;

  const fastThreshold = minPrice * 2;
  const isFastPassBid = !isFree && requestBase >= fastThreshold;

  useEffect(() => {
    if (isFree) return;
    if (requestBase >= fastThreshold && tier !== "fast") {
      setTier("fast");
    } else if (requestBase < fastThreshold && tier !== "standard") {
      setTier("standard");
    }
  }, [requestBase, fastThreshold, isFree, tier]);


  const priceForTier = (t: Tier) => {
    const base = roundPriceStep((Number(baseForTiles) || 0) * MULT[t]);
    if (base <= 0) return 0;
    const fee = round2(base * SITE_FEE_RATE);
    const total = roundPriceStep(base + fee);
    return total;
  };

  const djBaseDisplay = roundPriceStep(Number(dj?.pricePerRequest) || 0);
  const minTotalDisplay = isFree ? 0 : priceForTier("standard");


  const decCustom = () => {
    const v = Math.max(minPrice, round2((Number(baseAmount) || minPrice) - 0.5));
    setBaseAmount(String(v));
    setPricingMode("custom");
  };
  const incCustom = () => {
    const v = round2((Number(baseAmount) || minPrice) + 0.5);
    setBaseAmount(String(v));
    setPricingMode("custom");
  };

  const sanitizeMoney = (v: string) => {
    let s = v.replace(",", ".").replace(/[^0-9.]/g, "");
    const parts = s.split(".");
    if (parts.length > 2) s = parts[0] + "." + parts.slice(1).join("");
    if (s.includes(".")) {
      const [a, b] = s.split(".");
      s = a + "." + b.slice(0, 2);
    }
    return s;
  };

  const rememberOrdered = (trackId: string) => {
    const arr = JSON.parse(localStorage.getItem("rq_ordered_tracks") || "[]");
    const next = Array.from(new Set([...(arr as string[]), trackId])).slice(-20);
    localStorage.setItem("rq_ordered_tracks", JSON.stringify(next));
  };

  /** Автосоздание PaymentIntent */
  useEffect(() => {
    if (isFree) return;
    if (!dj?.slug) return;

    if (!selectedTrack) {
      setShowPayInline(false);
      setPayInlineLoading(false);
      setPiClientSecret(null);
      setCreatedOrderId(null);
      setShowCardForm(false);
      setPayError(null);
      return;
    }

    if (!stripePromise && STRIPE_PK) {
      console.warn("Stripe publishable key is missing on the frontend");
      return;
    }

    if (requestBase < minPrice) {
      setShowPayInline(false);
      setPayInlineLoading(false);
      setPiClientSecret(null);
      setCreatedOrderId(null);
      setShowCardForm(false);
      setPayError(null);
      return;
    }


    setShowPayInline(false);
    setPayInlineLoading(true);
    setPiClientSecret(null);
    setShowCardForm(false);
    setPayError(null);

    const reqId =
      (window.crypto?.randomUUID?.() || Math.random().toString(36)).replace(/-/g, "");
    (window as any).__rq_last_req = reqId;

    if (piDebounceRef.current) clearTimeout(piDebounceRef.current);
    piDebounceRef.current = setTimeout(async () => {
      try {
        const payload = {
          djSlug: dj.slug,
          searchQuery: selectedTrack.name,
          trackId: selectedTrack.id,
          amount: totalDisplay,
          requestAmount: requestBase,
          comment,
          trackImage: selectedTrack.image,
          trackArtists: selectedTrack.artists.map((a) => a.name),
          trackAlbum: selectedTrack.album,
          isPriority: tier === "priority",
          isFast: tier === "fast",
          tier,
          orderId: createdOrderId || undefined,
          clientNickname: clientNickname || undefined,
          clientInstagram: clientInstagram || undefined,
        };

        const { data } = await api.post<{ clientSecret: string; orderId: string }>(
          "/payments/create-payment-intent",
          payload
        );

        if ((window as any).__rq_last_req !== reqId) return;

        setPiClientSecret(data.clientSecret);
        setCreatedOrderId(data.orderId);
        setShowPayInline(true);
        setPayInlineLoading(false);
        setShowCardForm(false);
        setPayError(null);

        sessionStorage.setItem("lastDjSlug", dj.slug);

        const arr = JSON.parse(localStorage.getItem("rq_ordered_tracks") || "[]");
        const next = Array.from(new Set([...(arr as string[]), selectedTrack.id])).slice(-20);
        localStorage.setItem("rq_ordered_tracks", JSON.stringify(next));

        setTimeout(() => {
          inlinePayRef.current?.scrollIntoView({ behavior: "smooth", block: "start" });
        }, 50);
      } catch (e: any) {
        console.error(e);
        const msg = e?.response?.data?.msg || e?.message || "Error creating payment";
        setPayInlineLoading(false);
        setPayError(msg);
      }
    }, 350);


    return () => {
      if (piDebounceRef.current) clearTimeout(piDebounceRef.current);
    };
  }, [
    dj?.slug,
    selectedTrack?.id,
    selectedTrack?.name,
    requestBase,
    totalDisplay,
    tier,
    pricingMode,
    isFree,
    minPrice,
    stripePromise,
    currency,
  ]);


  if (notFound) {
    return (
      <div className="rq-container">
        <div className="rq-card" style={{ textAlign: "center", padding: "60px 24px" }}>
          <p style={{ fontSize: "1.2rem", fontWeight: 600, marginBottom: "12px" }}>
            This performer hasn't joined REQUEST yet
          </p>
          <Link to="/" style={{ color: "#22c55e", textDecoration: "underline" }}>
            Back to home
          </Link>
        </div>
      </div>
    );
  }

  /** === Tip payment intent === */
  const tipBase = (() => {
    const raw = Number(tipAmount || 5);
    return isNaN(raw) || raw < 1 ? 1 : roundPriceStep(raw);
  })();
  const tipFee = round2(tipBase * 0.15);
  const tipTotal = roundPriceStep(tipBase + tipFee);

  useEffect(() => {
    if (!tipMode || !dj?.slug || (!stripePromise && STRIPE_PK)) return;
    if (tipBase < 1) return;

    setTipShowPay(false);
    setTipClientSecret(null);
    setTipShowCard(false);
    setTipPayError(null);

    const reqId = (window.crypto?.randomUUID?.() || Math.random().toString(36)).replace(/-/g, "");
    (window as any).__rq_last_tip = reqId;

    if (tipDebounceRef.current) clearTimeout(tipDebounceRef.current);
    tipDebounceRef.current = setTimeout(async () => {
      try {
        const { data } = await api.post<{ clientSecret: string; orderId: string }>(
          "/payments/create-tip-intent",
          {
            djSlug: dj.slug,
            amount: tipTotal,
            requestAmount: tipBase,
            comment,
            clientNickname: clientNickname || undefined,
            clientInstagram: clientInstagram || undefined,
            orderId: tipOrderId || undefined,
          }
        );
        if ((window as any).__rq_last_tip !== reqId) return;
        setTipClientSecret(data.clientSecret);
        setTipOrderId(data.orderId);
        setTipShowPay(true);
        setTipPayError(null);
        sessionStorage.setItem("lastDjSlug", dj.slug);
        setTimeout(() => {
          tipPayRef.current?.scrollIntoView({ behavior: "smooth", block: "start" });
        }, 50);
      } catch (e: any) {
        console.error("Tip intent error", e);
        setTipPayError(e?.response?.data?.msg || "Error creating tip payment");
      }
    }, 400);

    return () => {
      if (tipDebounceRef.current) clearTimeout(tipDebounceRef.current);
    };
  }, [tipMode, dj?.slug, tipBase, tipTotal, stripePromise]);

  if (!dj) {
    return (
      <div className="rq-container">
        <div className="rq-card">
          <p style={{ textAlign: "center" }}>Loading...</p>
        </div>
      </div>
    );
  }

  const isReadyToSubmit =
    selectedTrack !== null && (isFree ? true : requestBase >= minPrice);


  const fastBaseStyle: React.CSSProperties = {
    borderColor: "#f5d06b",
    boxShadow: "0 0 10px rgba(245,208,107,.18)",
  };
  const fastSelectedStyle: React.CSSProperties = {
    borderColor: "#f5c542",
    boxShadow: "0 0 0 3px rgba(245,208,107,.28) inset, 0 0 12px rgba(245,208,107,.22)",
  };
  const fastCaptionStyle: React.CSSProperties = { color: "#f5d06b" };

  return (
    <div className="rq-container">
      {prevOrderIds.map((id, i) => {
        const order = prevOrders[id];
        if (!order) return null;
        return (
          <div
            key={id}
            className="rq-prev-order-banner"
            role="button"
            tabIndex={0}
            onClick={() => { window.location.href = `/success?order_id=${id}`; }}
            onKeyDown={(e) => { if (e.key === "Enter" || e.key === " ") window.location.href = `/success?order_id=${id}`; }}
          >
            <div className="rq-prev-order-inner">
              {order.trackImage && (
                <img className="rq-prev-order-img" src={order.trackImage} alt={order.searchQuery} />
              )}
              <div className="rq-prev-order-right">
                <div className="rq-prev-order-label">Requested Song: <strong>{order.searchQuery}</strong></div>
                <ThreeStepIndicator status={order.status as any} isPriority={(order.tier as any) === "fast"} />
              </div>
            </div>
          </div>
        );
      })}
      <div className="rq-card">
        <div className="rq-brand">
          {dj.avatarUrl ? (
            <img
              src={
                dj.avatarUrl.startsWith("http")
                  ? dj.avatarUrl
                  : `https://api.requesta.app${dj.avatarUrl}`
              }
              alt={dj.name}
              className="rq-logo"
              loading="lazy"
            />
          ) : (
            <div className="rq-logo rq-logo-fallback">
              {dj.name?.charAt(0)?.toUpperCase() || "D"}
            </div>
          )}

          <div className="rq-brand-title-row">
            <div className="rq-name">
              {dj.name}
              {dj.isAcceptingRequests === false && (
                <span style={{
                  fontSize: "0.6rem", fontWeight: 600, color: "#9ca3af", background: "rgba(107,114,128,0.15)",
                  padding: "2px 8px", borderRadius: 20, marginLeft: 8, verticalAlign: "middle", letterSpacing: "0.04em",
                }}>OFFLINE</span>
              )}
            </div>

            {dj.instagram && (
              <a
                href={`https://instagram.com/${dj.instagram}`}
                className="rq-ig"
                title={`@${dj.instagram}`}
                aria-label="Instagram"
                onClick={(e) => {
                  e.preventDefault();
                  openExternal(`https://instagram.com/${dj.instagram}`);
                }}
              >
                <FaInstagram size={16} />
              </a>
            )}
          </div>

          {dj.bio && (
            <div className="rq-bio">{dj.bio}</div>
          )}

          {isFree ? (
            <div className="rq-price-line">Request: Free</div>
          ) : (
            <>
              <div className="rq-price-line">
                Price per request: ${djMinBase.toFixed(2)}
              </div>
              <div className="rq-fastpass-line">
                Fast Pass <span className="rq-bolt-emoji">⚡</span>{" "}
                <span className="rq-fastpass-price">${fastPassBase.toFixed(2)}</span>
              </div>
            </>
          )}

          <button
            type="button"
            className="rq-tip-btn"
            onClick={() => {
              setTipMode((prev) => !prev);
              setTipAmount("5");
              setTipClientSecret(null);
              setTipOrderId(null);
              setTipShowPay(false);
              setTipShowCard(false);
              setTipPayError(null);
            }}
          >
            {tipMode ? "× Close Tip" : "+ Tip Only"}
          </button>
        </div>

        {tipMode && (
          <section className="rq-tip-section" ref={tipPayRef}>
            <div className="rq-bid-card">
              <div className="rq-bid-title">Tip {dj.name}</div>
              <div className="rq-bid-input-wrap">
                <span className="rq-bid-cur">$</span>
                <input
                  type="text"
                  inputMode="decimal"
                  enterKeyHint="done"
                  className="rq-bid-input"
                  value={tipAmount}
                  onChange={(e) => setTipAmount(sanitizeMoney(e.target.value))}
                  onBlur={(e) => {
                    const v = Number(e.target.value);
                    if (!v || v < 1) setTipAmount("1");
                  }}
                  placeholder="5"
                />
              </div>
            </div>

            <div className="rq-you-pay">
              You pay:{" "}
              <span className="rq-you-pay-amount">${tipTotal.toFixed(2)}</span>{" "}
              <span className="rq-you-pay-note">(incl. fees)</span>
            </div>

            {tipShowPay && stripePromise && tipClientSecret && (
              <Elements
                key={`tip-el-${tipClientSecret}-${tipTotal.toFixed(2)}`}
                stripe={stripePromise}
                options={{
                  clientSecret: tipClientSecret,
                  appearance: { theme: "night" },
                  locale: "en",
                }}
              >
                <div className="rq-inline-pay rq-inline-pay--inpaybox">
                  <InlinePayOptions
                    key={`tip-ipo-${tipClientSecret}-${tipTotal.toFixed(2)}`}
                    orderId={tipOrderId}
                    emailForLink={emailForLink}
                    setEmailForLink={setEmailForLink}
                    showCardForm={tipShowCard}
                    setShowCardForm={setTipShowCard}
                    processing={tipProcessing}
                    setProcessing={setTipProcessing}
                    payError={tipPayError}
                    setPayError={setTipPayError}
                    selectedTrack={null}
                    totalToCharge={tipTotal}
                    onPaymentSuccess={async (piId?: string) => {
                      if (!tipOrderId) return;
                      try {
                        await api.post("/payments/confirm-intent", {
                          orderId: tipOrderId,
                          piId,
                        });
                      } catch (e) {
                        console.error("confirm tip error", e);
                      }
                      setTipClientSecret(null);
                      setTipShowPay(false);
                      setTipMode(false);
                      window.location.href = `/success?order_id=${encodeURIComponent(tipOrderId)}`;
                    }}
                  />
                </div>
              </Elements>
            )}

            {tipShowPay && !STRIPE_PK && tipOrderId && (
              <div className="rq-inline-pay rq-inline-pay--inpaybox" style={{ padding: "16px", textAlign: "center" }}>
                <p style={{ marginBottom: 12, color: "#aaa", fontSize: 14 }}>Local Dev Mode</p>
                <button
                  style={{
                    background: "#1db954",
                    color: "#fff",
                    border: "none",
                    borderRadius: 8,
                    padding: "12px 32px",
                    fontSize: 16,
                    fontWeight: 600,
                    cursor: tipProcessing ? "not-allowed" : "pointer",
                    opacity: tipProcessing ? 0.6 : 1,
                  }}
                  disabled={tipProcessing}
                  onClick={async () => {
                    setTipProcessing(true);
                    try {
                      await api.post("/payments/mock-pay", { orderId: tipOrderId });
                      window.location.href = `/success?order_id=${encodeURIComponent(tipOrderId)}`;
                    } catch (e: any) {
                      setTipPayError(e?.response?.data?.msg || "Mock payment failed");
                      setTipProcessing(false);
                    }
                  }}
                >
                  {tipProcessing ? "Processing..." : `Mock Tip $${tipTotal.toFixed(2)}`}
                </button>
              </div>
            )}

            {tipPayError && !tipShowPay && (
              <div className="rq-pay-error">{tipPayError}</div>
            )}
          </section>
        )}

        {!tipMode && (
          <div className="rq-section-title" style={dj?.isAcceptingRequests === false ? { opacity: 0.5 } : undefined}>
            Request a Song :
          </div>
        )}
        {!tipMode && selectedTrack && (
          <div className="rq-selected-pill">
            <img src={selectedTrack.image} alt={selectedTrack.name} loading="lazy" />
            <div className="rq-pill-text" style={{ flex: 1, minWidth: 0 }}>
              <div className="rq-pill-line1">
                {selectedTrack.artists.map((a) => a.name).join(", ")}
              </div>
              <div className="rq-pill-line2">{truncate(selectedTrack.name, 20)}</div>
            </div>
            <button
              className="rq-pill-clear"
              onClick={() => {
                setSelectedTrack(null);
                setSearchQuery("");
                setShowPayInline(false);
                setPiClientSecret(null);
                setCreatedOrderId(null);
                setShowCardForm(false);
                setPayError(null);
              }}
            >
              ×
            </button>
          </div>
        )}

        {!tipMode && !selectedTrack && (
          <div className="rq-search-wrap">
            <FaSearch className="rq-search-ico" />
            <input
              ref={searchInputRef}
              type="search"
              placeholder="Search a Song"
              value={searchQuery}
              onFocus={() => {
                if (dj?.isAcceptingRequests === false) {
                  setShowOfflineBlock(true);
                  searchInputRef.current?.blur();
                }
              }}
              onChange={(e) => {
                if (dj?.isAcceptingRequests === false) {
                  setShowOfflineBlock(true);
                  return;
                }
                setSearchQuery(e.target.value);
                setSelectedTrack(null);
              }}
              className="rq-search-input"
              autoComplete="off"
              autoCorrect="off"
              autoCapitalize="none"
              spellCheck={false}
              inputMode="search"
              enterKeyHint="search"
              role="searchbox"
            />
            {searchQuery && (
              <button className="rq-clear" onClick={() => setSearchQuery("")} aria-label="Clear">
                ×
              </button>
            )}

            {results.length > 0 && (
              <ul className="rq-results" ref={resultsRef}>
                {results.map((track) => (
                  <li
                    key={track.id}
                    onClick={() => {
                      if (dj?.isAcceptingRequests === false) { setShowOfflineBlock(true); return; }
                      setSelectedTrack(track);
                      setSearchQuery(track.name);
                      setResults([]);
                      setJustSelected(true);
                      setBaseAmount(String(minPrice));
                      setPricingMode("custom");
                    }}
                  >
                    <img src={track.image} alt={track.name} loading="lazy" />
                    <div className="rq-res-text">
                      <div className="rq-res-title">{track.artists.map((a) => a.name).join(", ")}</div>
                      <div className="rq-res-sub">{track.name}</div>
                    </div>
                  </li>
                ))}
              </ul>
            )}
          </div>
        )}

        {!tipMode && !selectedTrack && (
          <>
            <div className="rq-tabs">
              <button
                className={`rq-tab ${activeTab === "for_you" ? "on" : ""}`}
                onClick={() => setActiveTab("for_you")}
              >
                For you
              </button>
              <button
                className={`rq-tab ${activeTab === "trending" ? "on" : ""}`}
                onClick={() => setActiveTab("trending")}
              >
                Trending
              </button>
              <button
                className={`rq-tab ${activeTab === "saved" ? "on" : ""}`}
                onClick={() => setActiveTab("saved")}
              >
                Saved
              </button>
            </div>

            <div className="rq-feed">
              {loadingFeed && <div className="rq-feed-skel">Loading…</div>}
              {!loadingFeed &&
                feed.map((t) => (
                  <div
                    key={t.id}
                    className="rq-feed-row"
                    role="button"
                    tabIndex={0}
                    onClick={() => {
                      if (dj?.isAcceptingRequests === false) { setShowOfflineBlock(true); return; }
                      setSelectedTrack(t);
                      setSearchQuery(t.name);
                      setBaseAmount(String(minPrice));
                      setPricingMode("custom");
                    }}
                    onKeyDown={(e) => {
                      if (e.key === "Enter" || e.key === " ") {
                        e.preventDefault();
                        (e.currentTarget as HTMLDivElement).click();
                      }
                    }}
                  >
                    <div className="rq-feed-left">
                      <img src={t.image} alt={t.name} loading="lazy" />
                      <div className="rq-feed-text">
                        <div className="rq-feed-title">{t.name}</div>
                        <div className="rq-feed-sub">
                          {t.artists.map((a) => a.name).join(", ")}
                        </div>
                      </div>
                    </div>

                    <button
                      className={`rq-feed-save ${savedIds.includes(t.id) ? "on" : ""}`}
                      title={savedIds.includes(t.id) ? "Remove from Saved" : "Save"}
                      onClick={(e) => {
                        e.stopPropagation();
                        toggleSaved(t.id);
                      }}
                    >
                      <FaBookmark />
                    </button>
                  </div>
                ))}
              {!loadingFeed && feed.length === 0 && (
                <div className="rq-feed-empty">No songs here yet</div>
              )}
            </div>
          </>
        )}

        {!tipMode && !isFree && selectedTrack && (
          <section className="rq-paybox" ref={inlinePayRef}>
            {!isFree && (
              <div className="rq-you-pay">
                You pay:{" "}
                <span className="rq-you-pay-amount">
                  ${totalDisplay.toFixed(2)}
                </span>{" "}
                <span className="rq-you-pay-note">(incl. fees)</span>
              </div>
            )}

            <div className="rq-bid-card">
              <div className="rq-bid-title">Place your Bid</div>

              <div className="rq-bid-input-wrap">
                {!bidFocused && !bidTouched && (
                  <span className="rq-bid-min-label">Min</span>
                )}

                <span className="rq-bid-cur">$</span>

                <input
                  type="text"
                  inputMode="decimal"
                  enterKeyHint="done"
                  className="rq-bid-input"
                  value={baseAmount}
                  onFocus={() => setBidFocused(true)}
                  onBlur={(e) => {
                    setBidFocused(false);
                    const v = Number(e.target.value);
                    if (!v || v < minPrice) {
                      setBaseAmount(String(minPrice));
                    }
                  }}
                  onChange={(e) => {
                    const val = sanitizeMoney(e.target.value);
                    setBaseAmount(val);

                    if (!bidTouched && val !== "" && Number(val) >= minPrice) {
                      setBidTouched(true);
                    }
                  }}
                  placeholder={String(minPrice)}
                />
              </div>

              {isFastPassBid && (
                <div className="rq-bid-fast-label">
                  Fast Pass ⚡ will be applied
                </div>
              )}
            </div>

            {payInlineLoading && !piClientSecret && (
              <div
                className="rq-inline-pay rq-inline-pay--inpaybox rq-pay-loading-placeholders"
                aria-busy="true"
                aria-label="Loading payment options"
              >
                {/* Same wrapper stack as InlinePayOptions so spacing matches the real pay block */}
                <div className="rq-inline-pay">
                  <div className="rq-wallets">
                    <div className="rq-express-vertical">
                      <div className="rq-fake-express-slot">
                        <span className="rq-fake-express-slot__icon" aria-hidden>
                          <svg width="16" height="20" viewBox="0 0 814 1000" fill="currentColor" focusable="false">
                            <path d="M788.1 340.9c-5.8 4.5-108.2 62.2-108.2 190.5 0 148.4 130.3 200.9 134.2 202.2-.6 3.2-20.7 71.9-68.7 141.9-42.8 61.6-87.5 123.1-155.5 123.1s-85.5-39.5-164-39.5c-76.5 0-103.3 39.5-164.4 39.5-68.1 0-120.8-60.4-163.7-122.2C37.1 790.2 0 663.3 0 538.5 0 344.3 126.2 227.2 279.1 227.2c68.1 0 124.4 40.7 167.2 40.7 40.2 0 103.2-43.2 190.8-43.2 30.8 0 142.1 2.7 242.1 108.2zM585.1 152.8c35.3-41.9 61.1-99.8 61.1-158.4 0-8.2-.6-16.5-1.9-24.8-58.1 2.4-126.2 38.9-168.1 87.5-32.3 36.8-61.9 94.8-61.9 153.4 0 8.9.7 17.7 2.1 25.8 4.7.4 9.5.6 14.3.6 47.4 0 93.4-24.7 134.5-63.1z" />
                          </svg>
                        </span>
                        <span className="rq-fake-express-slot__label">Pay</span>
                      </div>
                    </div>
                    <div className="rq-add-card" aria-hidden="true">
                      Add card
                    </div>
                  </div>
                </div>
              </div>
            )}

            {showPayInline && stripePromise && piClientSecret && (
              <Elements
                key={`el-${piClientSecret}-${totalDisplay.toFixed(2)}-${tier}`}
                stripe={stripePromise}
                options={{
                  clientSecret: piClientSecret,
                  appearance: { theme: "night" },
                  locale: "en",
                }}
              >
                <div className="rq-inline-pay rq-inline-pay--inpaybox">
                  <InlinePayOptions
                    key={`ipo-${piClientSecret}-${totalDisplay.toFixed(2)}-${tier}`}
                    orderId={createdOrderId}
                    emailForLink={emailForLink}
                    setEmailForLink={setEmailForLink}
                    showCardForm={showCardForm}
                    setShowCardForm={setShowCardForm}
                    processing={processing}
                    setProcessing={setProcessing}
                    payError={payError}
                    setPayError={setPayError}
                    selectedTrack={selectedTrack}
                    totalToCharge={totalDisplay}
                    onPaymentSuccess={async (piId?: string) => {
                      if (!createdOrderId) return;
                      try {
                        await api.post("/payments/confirm-intent", {
                          orderId: createdOrderId,
                          piId,
                        });
                      } catch (e) {
                        console.error("confirm-intent on RequestPage failed", e);
                      }
                      setPiClientSecret(null);
                      setShowPayInline(false);
                      window.location.href = `/success?order_id=${encodeURIComponent(
                        createdOrderId
                      )}`;
                    }}
                  />
                </div>
              </Elements>
            )}

            {showPayInline && !STRIPE_PK && createdOrderId && (
              <div className="rq-inline-pay rq-inline-pay--inpaybox" style={{ padding: "16px", textAlign: "center" }}>
                <p style={{ marginBottom: 12, color: "#aaa", fontSize: 14 }}>Local Dev Mode — Stripe is not configured</p>
                <button
                  style={{
                    background: "#1db954",
                    color: "#fff",
                    border: "none",
                    borderRadius: 8,
                    padding: "12px 32px",
                    fontSize: 16,
                    fontWeight: 600,
                    cursor: processing ? "not-allowed" : "pointer",
                    opacity: processing ? 0.6 : 1,
                  }}
                  disabled={processing}
                  onClick={async () => {
                    setProcessing(true);
                    try {
                      await api.post("/payments/mock-pay", { orderId: createdOrderId });
                      window.location.href = `/success?order_id=${encodeURIComponent(createdOrderId)}`;
                    } catch (e: any) {
                      setPayError(e?.response?.data?.msg || "Mock payment failed");
                      setProcessing(false);
                    }
                  }}
                >
                  {processing ? "Processing..." : `Mock Pay $${totalDisplay.toFixed(2)}`}
                </button>
                {payError && <p style={{ color: "red", marginTop: 8 }}>{payError}</p>}
              </div>
            )}
          </section>

        )}


        {!tipMode && isFree ? (
          <button
            onClick={async () => {
              if (!isReadyToSubmit || !selectedTrack) return;
              setProcessing(true);
              try {
                const payload = {
                  djSlug: dj.slug,
                  searchQuery: selectedTrack.name,
                  trackId: selectedTrack.id,
                  amount: totalDisplay,
                  requestAmount: requestBase,
                  comment,
                  trackImage: selectedTrack.image,
                  trackArtists: selectedTrack.artists.map((a) => a.name),
                  trackAlbum: selectedTrack.album,
                  isPriority: tier === "priority",
                  isFast: tier === "fast",
                  tier,
                  orderId: createdOrderId || undefined,
                  clientNickname: clientNickname || undefined,
                  clientInstagram: clientInstagram || undefined,
                };

                const { data } = await api.post("/orders/free", payload);
                if (data?.ok && data?.orderId) {
                  rememberOrdered(selectedTrack.id);
                  window.location.href = `/success?order_id=${encodeURIComponent(data.orderId)}`;
                } else {
                  alert("Something went wrong. Please try again.");
                }
              } catch (err: any) {
                console.error("Free request error", err);
                alert(err?.response?.data?.msg || "Server error creating free request");
              } finally {
                setProcessing(false);
              }
            }}
            className={`rq-cta ${isReadyToSubmit ? "on" : "off"}`}
            disabled={!isReadyToSubmit || processing}
          >
            {processing ? "Sending..." : "Request"}
          </button>
        ) : null}


        {!tipMode && (
          <>
            <label className="rq-checkbox">
              <input
                type="checkbox"
                checked={commentEnabled}
                onChange={(e) => setCommentEnabled(e.target.checked)}
              />
              <span>Add message to artist (optional)</span>
            </label>
            {commentEnabled && (
              <input
                className="rq-comment"
                type="text"
                placeholder="Type your message…"
                value={comment}
                onChange={(e) => setComment(e.target.value)}
              />
            )}

            {isFree ? (
              <p className="rq-fine">
                * Free request — your song will be sent to {dj.name} immediately.
              </p>
            ) : (
              <p className="rq-fine">
                * You will only be charged if your song is played by {dj.name}. If not, you will be
                refunded immediately.
              </p>
            )}
          </>
        )}

        {tipMode && (
          <p className="rq-fine">
            * Tips go directly to {dj.name}. No refunds on tips.
          </p>
        )}
      </div>

      {showCheckout && stripePromise && fetchClientSecret && (
        <div className="rq-embed-overlay" role="dialog" aria-label="Payment">
          <div className="rq-embed-card">
            <button className="rq-embed-close" onClick={() => setShowCheckout(false)} aria-label="Close">×</button>

            <div className="rq-embed-top">
              <div className="rq-embed-brand">
                <img src="/logo192.png" alt="Requesta" className="rq-embed-brand-logo" />
              </div>
              <div className="rq-embed-header">
                <div className="rq-embed-title-row">
                  {dj.avatarUrl ? (
                    <img
                      src={
                        dj.avatarUrl.startsWith("http")
                          ? dj.avatarUrl
                          : `https://api.requesta.app${dj.avatarUrl}`
                      }
                      alt={dj.name}
                      className="rq-embed-merchant-photo"
                      loading="lazy"
                    />
                  ) : (
                    <div className="rq-embed-merchant-photo rq-embed-merchant-fallback">
                      {dj.name?.charAt(0)?.toUpperCase() || "M"}
                    </div>
                  )}
                  <div className="rq-embed-title-wrap">
                    <div className="rq-embed-title">{dj.name}</div>
                  </div>
                </div>
                {selectedTrack && (
                  <div className="rq-embed-sub">
                    {selectedTrack.artists.map((a) => a.name).join(", ")} — {truncate(selectedTrack.name, 40)}
                  </div>
                )}
              </div>
            </div>

            <div className="rq-embed-grid">
              <div className="rq-embed-col">
                {selectedTrack ? (
                  <>
                    <div className="rq-embed-summary">
                      <img src={selectedTrack.image} alt={selectedTrack.name} loading="lazy" />
                      <div>
                        <div className="ttl">{selectedTrack.artists.map((a) => a.name).join(", ")}</div>
                        <div className="sub">{truncate(selectedTrack.name, 60)}</div>
                      </div>
                    </div>

                    {!isFree && (
                      <>
                        <div className="rq-embed-row"><span className="lab">Request</span><span className="val">${requestShown.toFixed(2)}</span></div>
                        <div className="rq-embed-row"><span className="lab">Fees</span><span className="val">${feesShown.toFixed(2)}</span></div>
                        <div className="rq-embed-row"><span className="lab">Total</span><span className="val">${totalDisplay.toFixed(2)}</span>
                        </div>
                        <div className="rq-embed-row">
                          <span className="lab">Tier</span>
                          <span className="val">{tier === "fast" ? "Fast Pass" : tier === "priority" ? "Priority" : "Standard"}</span>
                        </div>
                      </>
                    )}
                  </>
                ) : (
                  <div className="rq-embed-summary">
                    <div className="ttl">No track selected</div>
                    <div className="sub">Choose a song to proceed.</div>
                  </div>
                )}
              </div>

              <div className="rq-embed-col">
                <div id="embedded-checkout">
                  <EmbeddedCheckoutProvider
                    stripe={stripePromise}
                    options={{
                      fetchClientSecret,
                      onComplete: async () => {
                        if (embeddedSessionId) {
                          try { await api.post("/payments/confirm", { sessionId: embeddedSessionId }); }
                          catch (e) { console.error("confirm embedded session error", e); }
                          finally { window.location.href = `/success?session_id=${embeddedSessionId}`; }
                        }
                      },
                    }}
                  >
                    <EmbeddedCheckout />
                  </EmbeddedCheckoutProvider>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
      {showNicknamePrompt && (
        <div className="rq-nick-overlay" onClick={() => setShowNicknamePrompt(false)}>
          <div className="rq-nick-modal" onClick={(e) => e.stopPropagation()}>
            <div className="rq-nick-top">
              <img src="/logo192.png" alt="Requesta" className="rq-nick-app-logo" />
              <div className="rq-nick-merchant">
                {dj.avatarUrl ? (
                  <img
                    src={
                      dj.avatarUrl.startsWith("http")
                        ? dj.avatarUrl
                        : `https://api.requesta.app${dj.avatarUrl}`
                    }
                    alt={dj.name}
                    className="rq-nick-merchant-photo"
                    loading="lazy"
                  />
                ) : (
                  <div className="rq-nick-merchant-photo rq-nick-merchant-fallback">
                    {dj.name?.charAt(0)?.toUpperCase() || "M"}
                  </div>
                )}
                <div className="rq-nick-merchant-meta">
                  <strong>{dj.name}</strong>
                </div>
              </div>
            </div>

            <h3>What is your Nickname?</h3>
            <input
              type="text"
              placeholder="e.g. rockstar123 @insta"
              value={guestIdentityInput}
              onChange={(e) => setGuestIdentityInput(e.target.value.slice(0, 80))}
              autoFocus
            />
            <div className="rq-nick-actions">
              <button
                className="rq-nick-skip"
                onClick={() => {
                  setShowNicknamePrompt(false);
                  setGuestIdentityInput("");
                  setClientNickname("");
                  setClientInstagram("");
                  sessionStorage.setItem("rq_client_nickname", "");
                  sessionStorage.setItem("rq_client_instagram", "");
                }}
              >
                Skip
              </button>
              <button
                className="rq-nick-save"
                onClick={() => {
                  const parsed = parseGuestIdentity(guestIdentityInput);
                  setClientNickname(parsed.nickname);
                  setClientInstagram(parsed.instagram);
                  sessionStorage.setItem("rq_client_nickname", parsed.nickname);
                  sessionStorage.setItem("rq_client_instagram", parsed.instagram);
                  setShowNicknamePrompt(false);
                }}
              >
                Join session
              </button>
            </div>
          </div>
        </div>
      )}
      {/* Offline block modal */}
      {showOfflineBlock && dj && (
        <div
          role="dialog"
          aria-modal="true"
          style={{
            position: "fixed", inset: 0, background: "rgba(0,0,0,.7)", backdropFilter: "blur(4px)",
            display: "flex", alignItems: "center", justifyContent: "center", zIndex: 1000,
          }}
          onClick={() => setShowOfflineBlock(false)}
        >
          <div
            style={{
              background: "#111827", color: "#fff", border: "1px solid rgba(255,255,255,0.06)",
              borderRadius: 16, padding: 24, width: "min(92vw, 380px)", boxShadow: "0 20px 60px rgba(0,0,0,0.5)",
              textAlign: "center",
            }}
            onClick={(e) => e.stopPropagation()}
          >
            <h3 style={{ fontWeight: 700, fontSize: "1.1rem", margin: "0 0 10px" }}>
              {dj.name} is currently offline
            </h3>
            <p style={{ color: "#9ca3af", fontSize: "0.9rem", lineHeight: 1.5, marginBottom: 20 }}>
              They're not accepting requests right now. Come back when they're live.
            </p>
            <div style={{ display: "flex", gap: 10 }}>
              <button
                onClick={() => {
                  setShowOfflineBlock(false);
                  setTipMode(true);
                  setTipAmount("5");
                }}
                style={{
                  flex: 1, padding: "12px 14px", borderRadius: 10,
                  background: "linear-gradient(135deg, #22c55e, #16a34a)",
                  color: "#fff", border: "none", cursor: "pointer", fontWeight: 700, fontSize: "0.92rem",
                }}
              >
                Leave a Tip
              </button>
              <button
                onClick={() => setShowOfflineBlock(false)}
                style={{
                  flex: 1, padding: "12px 14px", borderRadius: 10,
                  background: "rgba(255,255,255,0.06)", color: "#9ca3af",
                  border: "1px solid rgba(255,255,255,0.08)", cursor: "pointer", fontWeight: 700, fontSize: "0.92rem",
                }}
              >
                Got it
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default RequestPage;
